import { useState } from 'react';
import { Search, X, Link } from 'lucide-react';
import type { CTASelectorProps } from './types';
import { getCTATypeLabel, getCTATypeColor } from './types';

export function CTASelector({ isOpen, onClose, onSelect, availableCTAs, currentCTAs, question }: CTASelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCTAs, setSelectedCTAs] = useState<string[]>([]);

  const filteredCTAs = availableCTAs.filter(cta =>
    !currentCTAs.includes(cta.id) &&
    (cta.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cta.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cta.cta_type.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleToggleCTA = (ctaId: string) => {
    setSelectedCTAs(prev =>
      prev.includes(ctaId) ? prev.filter(id => id !== ctaId) : [...prev, ctaId]
    );
  };

  const handleConfirm = () => {
    onSelect(selectedCTAs);
    setSelectedCTAs([]);
    setSearchTerm('');
    onClose();
  };

  const handleCancel = () => {
    setSelectedCTAs([]);
    setSearchTerm('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-[100] pt-12">
      <div className="bg-white  p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">
            Assign CTAs to: "{question.question}"
          </h3>
          <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search CTAs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300  focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto mb-4">
          {filteredCTAs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? 'No CTAs match your search.' : 'No available CTAs to assign.'}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredCTAs.map(cta => (
                <div
                  key={cta.id}
                  className={`p-3 border  cursor-pointer transition-colors ${selectedCTAs.includes(cta.id)
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
                    }`}
                  onClick={() => handleToggleCTA(cta.id)}
                >
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedCTAs.includes(cta.id)}
                      onChange={() => handleToggleCTA(cta.id)}
                      className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 "
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-900">{cta.name}</h4>
                        {cta.is_default && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Default CTA
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCTATypeColor(cta.cta_type)}`}>
                          {getCTATypeLabel(cta.cta_type)}
                        </span>
                        <span className="inline-flex items-center px-2 py-1  text-xs font-medium bg-indigo-100 text-indigo-800">
                          {cta.text}
                        </span>
                      </div>
                      {cta.description && (
                        <p className="text-sm text-gray-600 mt-1">{cta.description}</p>
                      )}
                      {cta.link && (
                        <div className="flex items-center mt-1">
                          <Link className="h-3 w-3 text-gray-400 mr-1" />
                          <span className="text-xs text-gray-500">{cta.link}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center pt-4 border-t">
          <p className="text-sm text-gray-600">
            {selectedCTAs.length} CTA{selectedCTAs.length !== 1 ? 's' : ''} selected
          </p>
          <div className="flex space-x-3">
            <button type="button" onClick={handleCancel} className="px-4 py-2 bg-gray-200 text-gray-800  hover:bg-gray-300">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={selectedCTAs.length === 0}
              className="px-4 py-2 bg-indigo-600 text-white  hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Assign {selectedCTAs.length} CTA{selectedCTAs.length !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
